"use client"

import { useState, useRef, useEffect } from "react"
import { useT } from "@lib/i18n/use-t"

/* ── Dropdown with checkboxes ── */
function FilterDropdown({
  label,
  options,
  selected,
  onToggle,
}: {
  label: string
  options: string[]
  selected: string[]
  onToggle: (val: string) => void
}) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node))
        setOpen(false)
    }
    document.addEventListener("mousedown", handler)
    return () => document.removeEventListener("mousedown", handler)
  }, [])

  const count = selected.length

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(!open)}
        className={`flex max-w-[60vw] items-center gap-1.5 whitespace-nowrap rounded-lg border px-3 py-1.5 text-xs font-medium transition
          ${
            count > 0
              ? "border-brand-light/30 bg-brand-light/8 text-brand-light dark:border-brand-dark/30 dark:bg-brand-dark/10 dark:text-brand-dark"
              : "border-slate-200 text-slate-500 hover:border-slate-300 dark:border-white/10 dark:text-slate-400 dark:hover:border-white/15"
          }`}
      >
        <span className="truncate">{label}</span>
        {count > 0 && (
          <span className="min-w-[16px] rounded-md bg-brand-light px-1 text-center text-[10px] font-bold text-white dark:bg-brand-dark dark:text-slate-950">
            {count}
          </span>
        )}
        <svg
          width="9"
          height="9"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          className={`transition-transform ${open ? "rotate-180" : ""}`}
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>

      {open && (
        <div className="absolute left-0 top-[calc(100%+5px)] z-50 min-w-[200px] max-h-[260px] overflow-y-auto rounded-xl border border-slate-200 bg-white p-1.5 shadow-xl dark:border-white/10 dark:bg-[#1a2035]">
          {options.map((opt) => {
            const active = selected.includes(opt)
            return (
              <div
                key={opt}
                onClick={() => onToggle(opt)}
                className={`flex cursor-pointer items-center gap-2 rounded-lg px-2.5 py-1.5 text-xs transition
                  ${
                    active
                      ? "bg-brand-light/8 font-semibold text-brand-light dark:bg-brand-dark/10 dark:text-brand-dark"
                      : "text-slate-700 hover:bg-slate-50 dark:text-slate-300 dark:hover:bg-white/5"
                  }`}
              >
                <div
                  className={`flex h-4 w-4 shrink-0 items-center justify-center rounded border-[1.5px] transition
                    ${
                      active
                        ? "border-brand-light bg-brand-light dark:border-brand-dark dark:bg-brand-dark"
                        : "border-slate-300 dark:border-slate-600"
                    }`}
                >
                  {active && (
                    <svg
                      width="9"
                      height="9"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="white"
                      strokeWidth="3"
                    >
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  )}
                </div>
                {opt}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

/* ── Active filter chip ── */
function Chip({
  label,
  onRemove,
}: {
  label: string
  onRemove: () => void
}) {
  return (
    <span
      onClick={onRemove}
      className="inline-flex cursor-pointer items-center gap-1 rounded-full border border-brand-light/20 bg-brand-light/8 px-2.5 py-0.5 text-[11px] font-medium text-brand-light transition hover:bg-brand-light/15 dark:border-brand-dark/20 dark:bg-brand-dark/10 dark:text-brand-dark"
    >
      {label}
      <svg
        width="11"
        height="11"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.5"
      >
        <line x1="18" y1="6" x2="6" y2="18" />
        <line x1="6" y1="6" x2="18" y2="18" />
      </svg>
    </span>
  )
}

/* ═══ MAIN ═══
 *
 * Controlled component: all filter state lives in URL (managed by
 * FilterBarWrapper). This component only renders + emits change events.
 *
 * NOTE: Package / Voltage / Power filters were removed temporarily —
 * the RadioLED Excel importer (workflows/import-price-list.ts) does not
 * yet write `package`/`voltage`/`power` keys into product metadata.
 * They will return once the backend importer is enriched (see report).
 */
export default function FilterBar({
  sortBy,
  onSortChange,
  view,
  onViewChange,
  manufacturers,
  selectedManufacturers,
  onToggleManufacturer,
  stockOnly,
  saleOnly,
  onTabChange,
  onClearAll,
}: {
  sortBy: string
  onSortChange: (val: string) => void
  view: "table" | "grid"
  onViewChange: (val: "table" | "grid") => void
  manufacturers: string[]
  selectedManufacturers: string[]
  onToggleManufacturer: (val: string) => void
  stockOnly: boolean
  saleOnly: boolean
  onTabChange: (tab: "all" | "stock" | "sale") => void
  onClearAll: () => void
}) {
  const t = useT()

  const allFilters: Array<{ t: "brand" | "stock" | "sale"; v: string }> = [
    ...selectedManufacturers.map(
      (v) => ({ t: "brand" as const, v })
    ),
    ...(stockOnly ? [{ t: "stock" as const, v: t("inStock") }] : []),
    ...(saleOnly ? [{ t: "sale" as const, v: t("promo") }] : []),
  ]

  const rmFilter = (f: { t: "brand" | "stock" | "sale"; v: string }) => {
    if (f.t === "brand") onToggleManufacturer(f.v)
    if (f.t === "stock") onTabChange("all")
    if (f.t === "sale") onTabChange("all")
  }

  return (
    <div className="overflow-hidden rounded-2xl border border-slate-200/60 bg-white p-3 dark:border-white/8 dark:bg-[#111827]">
      {/* Row 1: Tabs + sort + view toggle */}
      <div className="mb-2 flex flex-wrap items-center gap-x-1.5 gap-y-2">
        <button
          onClick={() => onTabChange("all")}
          className={`whitespace-nowrap rounded-full px-3 py-1 text-xs font-medium transition
            ${!stockOnly && !saleOnly
              ? "bg-brand-light/10 text-brand-light dark:bg-brand-dark/10 dark:text-brand-dark"
              : "text-slate-400"
            }`}
        >
          {t("allProducts")}
        </button>
        <button
          onClick={() => onTabChange(stockOnly ? "all" : "stock")}
          className={`whitespace-nowrap rounded-full px-3 py-1 text-xs font-medium transition
            ${stockOnly
              ? "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400"
              : "text-slate-400"
            }`}
        >
          ● {t("inStock")}
        </button>
        <button
          onClick={() => onTabChange(saleOnly ? "all" : "sale")}
          className={`whitespace-nowrap rounded-full px-3 py-1 text-xs font-medium transition
            ${saleOnly
              ? "bg-red-500/10 text-red-600 dark:text-red-400"
              : "text-slate-400"
            }`}
        >
          🏷 {t("promo")}
        </button>

        <div className="ml-auto flex items-center gap-2">
          <select
            value={sortBy}
            onChange={(e) => onSortChange(e.target.value)}
            className="max-w-[40vw] rounded-lg border border-slate-200 bg-white px-2 py-1 text-xs outline-none dark:border-white/10 dark:bg-[#111827] dark:text-white"
          >
            <option value="created_at">{t("sortNew")}</option>
            <option value="price_asc">{t("sortPriceUp")}</option>
            <option value="price_desc">{t("sortPriceDown")}</option>
          </select>
          <div className="flex gap-1">
            {(["table", "grid"] as const).map((v) => (
              <button
                key={v}
                onClick={() => onViewChange(v)}
                className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-md border text-sm transition
                  ${
                    view === v
                      ? "border-brand-light/20 bg-brand-light/8 text-brand-light dark:border-brand-dark/20 dark:bg-brand-dark/10 dark:text-brand-dark"
                      : "border-slate-200 text-slate-400 dark:border-white/10"
                  }`}
              >
                {v === "table" ? "☰" : "▦"}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Row 2: Parametric dropdowns
          Currently only Manufacturer is real — Package/Voltage/Power are
          hidden until product metadata is enriched by the importer. */}
      {manufacturers.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5">
          <div className="max-w-full">
            <FilterDropdown
              label={t("manufacturer")}
              options={manufacturers}
              selected={selectedManufacturers}
              onToggle={onToggleManufacturer}
            />
          </div>
        </div>
      )}

      {/* Row 3: Active filter chips */}
      {allFilters.length > 0 && (
        <div className="mt-2 flex flex-wrap items-center gap-1.5">
          {allFilters.map((f) => (
            <Chip key={`${f.t}-${f.v}`} label={f.v} onRemove={() => rmFilter(f)} />
          ))}
          <button
            onClick={onClearAll}
            className="ml-1 text-[11px] font-medium text-red-500 transition hover:text-red-600"
          >
            {t("clearFilters")} ✕
          </button>
        </div>
      )}
    </div>
  )
}
